import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, Route } from 'react-router';
import { ProjectIndexRedirect } from '@gonfalon/chrome/project-index-redirect';
import { PaVisualizationRedirect } from '@gonfalon/product-analytics/components/PaVisualizationRedirect';
import { useMatchingAppPathPattern } from '@gonfalon/router/useMatchingPathPattern';
import { setRouteContext } from '@gonfalon/telemetry/setRouteContext';

import { AppShell } from '#components/AppShell';

import { appRouteOwnership } from './appRouteOwnership';

const ApplicationsListContainer = async () =>
  import(/* webpackChunkName: "applications.list" */ '#routes/applications/index');

const ProjectLayout = async () => import(/* webpackChunkName: "ProjectLayout" */ '#routes/app/projects.$projectKey_');

export const appRoutes = () => (
  <Route element={<OwnershipWatcher />}>
    {/* renders fullpage PDF download view without navigation and therefore needs to be outside the appshell */}
    <Route
      path="/projects/:projectKey/experiments/:experimentKey/download-pdf"
      lazy={async () =>
        import(
          /* webpackChunkName: "ExperimentDownloadPDF" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.download-pdf'
        )
      }
    />
    <Route element={<AppShell />}>
      <Route path="/" lazy={async () => import(/* webpackChunkName: "Root" */ '#routes/app/root')}>
        <Route
          path="confirm-auth/:deviceCode"
          lazy={async () => import(/* webpackChunkName: "ConfirmAuth" */ '#routes/app/confirm-auth.$deviceCode')}
        />
        <Route
          path="foundation-cost-calculator"
          lazy={async () =>
            import(/* webpackChunkName: "foundationCostCalculator" */ '#routes/app/foundation-cost-calculator')
          }
        />
        <Route
          path="insights"
          lazy={async () => import(/* webpackChunkName: "launchInsights.entry" */ '#routes/app/insights')}
        />
        <Route
          path="launchlitely"
          lazy={async () => import(/* webpackChunkName: "launchlitely" */ '#routes/app/launchlitely')}
        />
        <Route
          path="launchlitely/:projectKey"
          lazy={async () => import(/* webpackChunkName: "launchlitely" */ '#routes/app/launchlitely')}
        />
        <Route
          path="review/:owner/:repository/pull/:pullNumber"
          lazy={async () =>
            import(
              /* webpackChunkName: "ReleaseAutopilotReview" */ '#routes/app/review.$owner.$repository.pull.$pullNumber'
            )
          }
        />

        <Route
          path="product-analytics/integrations/:warehouseId"
          lazy={async () =>
            import(
              /* webpackChunkName: "productAnalyticsIntegrationsPage" */ '#routes/app/product-analytics-integration'
            )
          }
        />

        <Route
          id="project-context"
          path="/projects/:projectKey"
          lazy={async () => import(/* webpackChunkName: "ProjectSection"*/ '#routes/app/projects.$projectKey')}
        >
          <Route lazy={ProjectLayout}>
            <Route
              path="product-analytics"
              lazy={async () =>
                import(
                  /* webpackChunkName: "product-analytics-container" */ '#routes/app/projects.$projectKey.product-analytics_'
                )
              }
            >
              <Route index element={<Navigate to="dashboards" replace />} />
              <Route path="charts">
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-charts-new" */ '#routes/app/projects.$projectKey.product-analytics.charts.new'
                    )
                  }
                />
                <Route
                  path=":id"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-charts-new" */ '#routes/app/projects.$projectKey.product-analytics.charts.$id'
                    )
                  }
                />
              </Route>
              {/* Compatibility redirects for old visualization URLs */}
              {/* TODO: remove this after 30 days - OCT 24th */}
              <Route path="visualizations/new" element={<PaVisualizationRedirect />} />
              <Route path="visualizations/:id" element={<PaVisualizationRedirect target="id" />} />
              <Route path="events">
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-events" */ '#routes/app/projects.$projectKey.product-analytics.events'
                    )
                  }
                />
              </Route>
              <Route
                path="cohorts"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "product-analytics-cohorts-container" */ '#routes/app/projects.$projectKey.product-analytics.cohorts_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-cohorts" */ '#routes/app/projects.$projectKey.product-analytics.cohorts'
                    )
                  }
                />
                <Route
                  path=":id"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-cohort-details-container" */ '#routes/app/projects.$projectKey.product-analytics.cohorts.$id_'
                    )
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "product-analytics-cohort-details" */ '#routes/app/projects.$projectKey.product-analytics.cohorts.$id'
                      )
                    }
                  />
                  <Route
                    path="edit"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "product-analytics-cohort-edit" */ '#routes/app/projects.$projectKey.product-analytics.cohorts.$id.edit'
                      )
                    }
                  />
                </Route>
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-cohort-new" */ '#routes/app/projects.$projectKey.product-analytics.cohorts.new'
                    )
                  }
                />
              </Route>
              <Route
                path="user-attributes"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "product-analytics-user-attributes" */ '#routes/app/projects.$projectKey.product-analytics.user-attributes'
                  )
                }
              />
              <Route
                path="user-activity"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "product-analytics-user-activity" */ '#routes/app/projects.$projectKey.product-analytics.user-activity'
                  )
                }
              />
              <Route
                path="dashboards"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "product-analytics-dashboards-container" */ '#routes/app/projects.$projectKey.product-analytics.dashboards_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-dashboards" */ '#routes/app/projects.$projectKey.product-analytics.dashboards'
                    )
                  }
                />
                <Route
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-dashboards" */ '#routes/app/projects.$projectKey.product-analytics.dashboards'
                    )
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "product-analytics-dashboards-new" */ '#routes/app/projects.$projectKey.product-analytics.dashboards.new'
                      )
                    }
                  />
                </Route>
                <Route
                  path=":id"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "product-analytics-dashboard-details" */ '#routes/app/projects.$projectKey.product-analytics.dashboards.$id'
                    )
                  }
                />
              </Route>
            </Route>
          </Route>

          <Route
            path="audit"
            lazy={async () =>
              import(/* webpackChunkName: "AuditLogComponent" */ '#routes/app/projects.$projectKey.audit')
            }
          />
          <Route
            path="audit/:auditId/*"
            lazy={async () =>
              import(/* webpackChunkName: "AuditLogEntryComponent" */ '#routes/app/projects.$projectKey.audit.$auditId')
            }
          />
          <Route lazy={ProjectLayout}>
            <Route index element={<ProjectIndexRedirect />} />
            <Route
              path="home"
              lazy={async () => import(/* webpackChunkName: "Home" */ '#routes/app/projects.$projectKey.home')}
            />
            {/* Pathless, so useRouteNavMode can answer which nav a page belongs to without
                matching its URL. It cannot go on the routes themselves: they are lazy and
                their modules already export a handle. */}
            <Route handle={{ navMode: 'code' }}>
              <Route
                path="guarded-rollouts"
                lazy={async () =>
                  import(/* webpackChunkName: "GuardedRollouts" */ '#routes/app/projects.$projectKey.guarded-rollouts')
                }
              />
            </Route>
            <Route
              path="factory/release-coordinator"
              lazy={async () =>
                import(
                  /* webpackChunkName: "ReleaseCoordinatorDashboard" */ '#routes/app/projects.$projectKey.factory.release-coordinator'
                )
              }
            />
            {/* The Agents nav drops these, so they belong to code even though nothing in
                their URL says so. Left undeclared, the stored preference decides instead,
                and a member in agent mode opening one of these lands on a page their nav
                has no link to. */}
            <Route handle={{ navMode: 'code' }}>
              <Route
                path="sessions"
                lazy={async () =>
                  import(/* webpackChunkName: "ObservabilitySessions" */ '#routes/app/projects.$projectKey.sessions')
                }
              >
                <Route index />
                <Route
                  path=":sessionId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ObservabilitySessionDetail" */ '#routes/app/projects.$projectKey.sessions.$sessionId'
                    )
                  }
                />
              </Route>
              <Route
                path="errors"
                lazy={async () =>
                  import(/* webpackChunkName: "ObservabilityErrors" */ '#routes/app/projects.$projectKey.errors')
                }
              >
                <Route index />
                <Route path=":issueId">
                  <Route index />
                  <Route path="instances" />
                  <Route path="instances/:instanceId" />
                </Route>
              </Route>
            </Route>
            <Route
              path="dashboards"
              lazy={async () =>
                import(
                  /* webpackChunkName: "ObservabilityDashboardsLayout" */ '#routes/app/projects.$projectKey.dashboards_'
                )
              }
            >
              <Route
                index
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ObservabilityDashboards" */ '#routes/app/projects.$projectKey.dashboards'
                  )
                }
              />
              <Route
                path="new"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ObservabilityDashboardNew" */ '#routes/app/projects.$projectKey.dashboard.new'
                  )
                }
              />
              <Route
                path=":dashboardId"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ObservabilityDashboardLayout" */ '#routes/app/projects.$projectKey.dashboard_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ObservabilityDashboard" */ '#routes/app/projects.$projectKey.dashboard'
                    )
                  }
                />
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ObservabilityDashboardNew" */ '#routes/app/projects.$projectKey.dashboard.new'
                    )
                  }
                />
                <Route
                  path=":graphId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ObservabilityGraphLayout" */ '#routes/app/projects.$projectKey.dashboard.graph_'
                    )
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ObservabilityDashboardGraph" */ '#routes/app/projects.$projectKey.dashboard.graph'
                      )
                    }
                  />
                  <Route
                    path="edit"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ObservabilityDashboardGraphEdit" */ '#routes/app/projects.$projectKey.dashboard.graph.edit'
                      )
                    }
                  />
                </Route>
              </Route>
            </Route>
            {/* Code-only, same as sessions and errors above. */}
            <Route handle={{ navMode: 'code' }}>
              <Route
                path="logs"
                lazy={async () =>
                  import(/* webpackChunkName: "ObservabilityLogs" */ '#routes/app/projects.$projectKey.logs')
                }
              />
            </Route>
            <Route
              path="observability-metrics"
              lazy={async () =>
                import(
                  /* webpackChunkName: "ObservabilityMetrics" */ '#routes/app/projects.$projectKey.observability-metrics'
                )
              }
            />
            <Route
              path="traces"
              lazy={async () =>
                import(/* webpackChunkName: "ObservabilityTraces" */ '#routes/app/projects.$projectKey.traces')
              }
            />
            <Route
              path="alerts"
              lazy={async () =>
                import(/* webpackChunkName: "ObservabilityAlertsMeta" */ '#routes/app/projects.$projectKey.alerts_')
              }
            >
              <Route
                index
                lazy={async () =>
                  import(/* webpackChunkName: "ObservabilityAlerts" */ '#routes/app/projects.$projectKey.alerts')
                }
              />
              <Route
                path="new"
                lazy={async () =>
                  import(/* webpackChunkName: "ObservabilityAlertsNew" */ '#routes/app/projects.$projectKey.alerts.new')
                }
              />
              <Route
                path=":alertId"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ObservabilityAlertsViewMeta" */ '#routes/app/projects.$projectKey.alerts.$alertId_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ObservabilityAlertsView" */ '#routes/app/projects.$projectKey.alerts.$alertId'
                    )
                  }
                />
                <Route
                  path="edit"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ObservabilityAlertsEdit" */ '#routes/app/projects.$projectKey.alerts.$alertId.edit'
                    )
                  }
                />
              </Route>
            </Route>
            <Route handle={{ navMode: 'code' }}>
              <Route
                path="flags"
                lazy={async () => import(/* webpackChunkName: "Flags" */ '#routes/app/projects.$projectKey.flags_')}
              >
                <Route
                  path="connect"
                  lazy={async () =>
                    import(/* webpackChunkName: "FlagsConnect" */ '#routes/app/projects.$projectKey.flags.connect')
                  }
                />
                <Route
                  lazy={async () =>
                    import(/* webpackChunkName: "FlagsModes" */ '#routes/app/projects.$projectKey.flags.modes')
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(/* webpackChunkName: "FlagsDashboard" */ '#routes/app/projects.$projectKey.flags')
                    }
                  />
                  <Route
                    lazy={async () =>
                      import(/* webpackChunkName: "FlagsDashboard" */ '#routes/app/projects.$projectKey.flags')
                    }
                  >
                    <Route
                      path="new"
                      lazy={async () =>
                        import(/* webpackChunkName: "FlagsNew" */ '#routes/app/projects.$projectKey.flags.new')
                      }
                    />
                    <Route
                      path=":flagKey/environment-overview"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagsEnvironmentOverview" */ '#routes/app/projects.$projectKey.flags.$flagKey.environment-overview'
                        )
                      }
                    />
                  </Route>
                </Route>
                <Route
                  path=":flagKey/approvals/:approvalRequestId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "FlagsApprovalRequest" */ '#routes/app/projects.$projectKey.flags.$flagKey.approvals.$approvalRequestId'
                    )
                  }
                />
                <Route
                  path=":flagKey"
                  lazy={async () =>
                    import(/* webpackChunkName: "FlagEntityHome" */ '#routes/app/projects.$projectKey.flags.$flagKey')
                  }
                >
                  <Route index element={<Navigate to="targeting" replace />} />
                  <Route
                    path="history"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagHistory" */ '#routes/app/projects.$projectKey.flags.$flagKey.history'
                      )
                    }
                  />
                  <Route
                    path="add-to-code"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagAddToCode" */ '#routes/app/projects.$projectKey.flags.$flagKey.add-to-code'
                      )
                    }
                  />
                  <Route
                    path="code"
                    lazy={async () =>
                      import(/* webpackChunkName: "FlagCode" */ '#routes/app/projects.$projectKey.flags.$flagKey.code')
                    }
                  />
                  <Route
                    path="history/:auditId/*"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagHistoryAuditEntryHome" */ '#routes/app/projects.$projectKey.flags.$flagKey.history.$auditId'
                      )
                    }
                  />
                  <Route
                    path="targeting"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagTargeting"*/ '#routes/app/projects.$projectKey.flags.$flagKey.targeting'
                      )
                    }
                  >
                    <Route
                      path="exclude-rules"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagTargetingExcludeRules"*/ '#routes/app/projects.$projectKey.flags.$flagKey.targeting.exclude-rules'
                        )
                      }
                    />
                    <Route
                      path="pending-changes"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagTargetingPendingChanges"*/ '#routes/app/projects.$projectKey.flags.$flagKey.targeting.pending-changes'
                        )
                      }
                    />
                    <Route
                      path="pending-changes/:pendingId"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagTargetingPendingChange"*/ '#routes/app/projects.$projectKey.flags.$flagKey.targeting.pending-changes.$pendingId'
                        )
                      }
                    />
                    <Route
                      path="expiring-targets"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagTargetingExpiringTargets"*/ '#routes/app/projects.$projectKey.flags.$flagKey.targeting.expiring-targets'
                        )
                      }
                    />
                    <Route
                      path="expiring-targets/:expiringId"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagTargetingExpiringTarget"*/ '#routes/app/projects.$projectKey.flags.$flagKey.targeting.expiring-targets.$expiringId'
                        )
                      }
                    />
                    <Route
                      path="conflicts/:conflictIndex"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagTargetingConflict"*/ '#routes/app/projects.$projectKey.flags.$flagKey.targeting.conflicts.$conflictIndex'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="settings"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagSettingsIndex"*/ '#routes/app/projects.$projectKey.flags.$flagKey.settings'
                      )
                    }
                  >
                    <Route
                      path="archive"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagSettingsArchive" */ '#routes/app/projects.$projectKey.flags.$flagKey.settings.archive'
                        )
                      }
                    />
                    <Route
                      path="deprecate"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagSettingsDeprecate" */ '#routes/app/projects.$projectKey.flags.$flagKey.settings.deprecate'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="environments"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagEnvironmentSettingsIndex"*/ '#routes/app/projects.$projectKey.flags.$flagKey.environments'
                      )
                    }
                  >
                    <Route
                      path="exclude-rules"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagEnvironmentSettingsExcludeRules"*/ '#routes/app/projects.$projectKey.flags.$flagKey.environments.exclude-rules'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="variations"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagVariationSettings"*/ '#routes/app/projects.$projectKey.flags.$flagKey.variations'
                      )
                    }
                  >
                    <Route
                      path="pending-changes"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagVariationPendingChanges"*/ '#routes/app/projects.$projectKey.flags.$flagKey.variations.pending-changes'
                        )
                      }
                    />
                    <Route
                      path="pending-changes/:pendingId"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagVariationPendingChange"*/ '#routes/app/projects.$projectKey.flags.$flagKey.variations.pending-changes.$pendingId'
                        )
                      }
                    />
                    <Route
                      path="conflicts/:conflictIndex"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagVariationConflict"*/ '#routes/app/projects.$projectKey.flags.$flagKey.variations.conflicts.$conflictIndex'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="insights"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagInsights" */ '#routes/app/projects.$projectKey.flags.$flagKey.insights'
                      )
                    }
                  />
                  <Route
                    path="overview"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagOverview" */ '#routes/app/projects.$projectKey.flags.$flagKey.overview'
                      )
                    }
                  />
                  <Route
                    path="monitoring"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagMonitoring" */ '#routes/app/projects.$projectKey.flags.$flagKey.monitoring'
                      )
                    }
                  />
                  <Route
                    path="audience"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagAudience" */ '#routes/app/projects.$projectKey.flags.$flagKey.audience'
                      )
                    }
                  />
                  <Route
                    path="feedback"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagUserFeedback" */ '#routes/app/projects.$projectKey.flags.$flagKey.user-feedback'
                      )
                    }
                  />
                  <Route
                    path="workflows"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagWorkflows" */ '#routes/app/projects.$projectKey.flags.$flagKey.workflows'
                      )
                    }
                  >
                    <Route
                      index
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagWorkflows" */ '#routes/app/projects.$projectKey.flags.$flagKey.workflows.index'
                        )
                      }
                    />
                    <Route
                      path="new"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagWorkflowCreate" */ '#routes/app/projects.$projectKey.flags.$flagKey.workflows.new'
                        )
                      }
                    />
                    <Route
                      path=":workflowId"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "FlagWorkflowHome" */ '#routes/app/projects.$projectKey.flags.$flagKey.workflows.$workflowId'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="code-refs"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "FlagCodeReferences" */ '#routes/app/projects.$projectKey.flags.$flagKey.code-refs'
                      )
                    }
                  />
                  <Route path="experiments/*" element={<Navigate to="../targeting" replace />} />
                  <Route path="links" element={<Navigate to="../targeting" replace />} />
                </Route>
              </Route>
            </Route>
            <Route path="compare">
              <Route
                index
                lazy={async () =>
                  import(/* webpackChunkName: "FlagsCompare" */ '#routes/app/projects.$projectKey.compare')
                }
              />
              <Route
                path=":environmentPair"
                lazy={async () =>
                  import(/* webpackChunkName: "FlagsCompare" */ '#routes/app/projects.$projectKey.compare')
                }
              >
                <Route
                  path="flags/:flagKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "FlagsCompare" */ '#routes/app/projects.$projectKey.compare.$environments.flags.$flagKey'
                    )
                  }
                />
              </Route>
            </Route>
            <Route handle={{ navMode: 'code' }}>
              <Route
                path="releases"
                lazy={async () =>
                  import(/* webpackChunkName: "ReleasesLayout" */ '#routes/app/projects.$projectKey.releases')
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(/* webpackChunkName: "ReleasesIndex" */ '#routes/app/projects.$projectKey.releases.index')
                  }
                />
                <Route
                  path=":releasePipelineKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "Releases" */ '#routes/app/projects.$projectKey.releases.$releasePipelineKey'
                    )
                  }
                />
              </Route>
            </Route>
            <Route handle={{ navMode: 'agent' }}>
              <Route
                path="agentcontrol"
                lazy={async () =>
                  import(/* webpackChunkName: "AgentControlOptIn" */ '#routes/app/projects.$projectKey.agentcontrol')
                }
              />
              <Route
                path="agentcontrol-welcome"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "AgentcontrolWelcomeLayout" */ '#routes/app/projects.$projectKey.agentcontrol-welcome'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AgentcontrolWelcome" */ '#routes/app/projects.$projectKey.agentcontrol-welcome.index'
                    )
                  }
                />
                <Route
                  path="setup-sdk"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AgentcontrolWelcomeSetupSdk" */ '#routes/app/projects.$projectKey.agentcontrol-welcome.setup-sdk'
                    )
                  }
                />
              </Route>
              <Route
                path="ai-configs"
                lazy={async () =>
                  import(/* webpackChunkName: "AiConfigs" */ '#routes/app/projects.$projectKey.ai-configs')
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiConfigsIndex" */ '#routes/app/projects.$projectKey.ai-configs.ai-configs.index'
                    )
                  }
                />
                <Route
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiConfigsIndex" */ '#routes/app/projects.$projectKey.ai-configs.ai-configs.index'
                    )
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(/* webpackChunkName: "AiConfigNew" */ '#routes/app/projects.$projectKey.ai-configs.new')
                    }
                  />
                </Route>
                {/* Compatibility redirects: leftover /ai-configs collection paths now live in AI Library */}
                <Route
                  path="model-configs"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiConfigsModelConfigsRedirect" */ '#routes/app/projects.$projectKey.ai-configs.model-configs'
                    )
                  }
                />
                <Route
                  path="model-configs/:configKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiConfigsModelConfigRedirect" */ '#routes/app/projects.$projectKey.ai-configs.model-configs.$modelConfigKey'
                    )
                  }
                />
                <Route
                  path="prompt-snippets"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiConfigsPromptSnippetsRedirect" */ '#routes/app/projects.$projectKey.ai-configs.prompt-snippets'
                    )
                  }
                />
                <Route
                  path="prompt-snippets/:snippetKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiConfigsPromptSnippetRedirect" */ '#routes/app/projects.$projectKey.ai-configs.prompt-snippets.$snippetKey'
                    )
                  }
                />
                <Route
                  path=":configKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiConfigDetails" */ '#routes/app/projects.$projectKey.ai-configs.$aiConfigKey'
                    )
                  }
                >
                  <Route index element={<Navigate to="variations" replace />} />
                  <Route
                    path="variations"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiConfigVariations" */ '#routes/app/projects.$projectKey.ai-configs.$aiConfigKey.variations'
                      )
                    }
                  />

                  <Route
                    path="variations/:variationKey"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiConfigVariationCompareView" */ '#routes/app/projects.$projectKey.ai-configs.$aiConfigKey.variations.$variationKey'
                      )
                    }
                  />

                  <Route
                    path="targeting"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiConfigTargeting" */ '#routes/app/projects.$projectKey.ai-configs.$aiConfigKey.targeting'
                      )
                    }
                  >
                    <Route
                      path="pending-changes"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "AiConfigPendingChanges" */ '#routes/app/projects.$projectKey.ai-configs.$aiConfigKey.targeting.pending-changes'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="monitoring"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiConfigMonitoring" */ '#routes/app/projects.$projectKey.ai-configs.$aiConfigKey.monitoring'
                      )
                    }
                  />
                  <Route
                    path="playground"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiConfigPlayground" */ '#routes/app/projects.$projectKey.ai-configs.$aiConfigKey.playground'
                      )
                    }
                  />
                </Route>
              </Route>
              <Route
                path="ai-monitoring"
                lazy={async () =>
                  import(/* webpackChunkName: "AiMonitoring" */ '#routes/app/projects.$projectKey.ai-monitoring')
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiMonitoringTrends" */ '#routes/app/projects.$projectKey.ai-monitoring.trends'
                    )
                  }
                />
                <Route
                  path="trends"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiMonitoringTrends" */ '#routes/app/projects.$projectKey.ai-monitoring.trends'
                    )
                  }
                />
              </Route>
              <Route
                path="ai-library"
                lazy={async () =>
                  import(/* webpackChunkName: "AiLibraryLayout" */ '#routes/app/projects.$projectKey.ai-library_')
                }
              >
                <Route index element={<Navigate to="models" replace />} />
                <Route
                  path="models"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiLibraryModels" */ '#routes/app/projects.$projectKey.ai-library.models'
                    )
                  }
                />
                <Route
                  path="models/:configKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiLibraryModel" */ '#routes/app/projects.$projectKey.ai-library.models.$configKey'
                    )
                  }
                />
                <Route
                  path="tools"
                  lazy={async () =>
                    import(/* webpackChunkName: "AiLibraryTools" */ '#routes/app/projects.$projectKey.ai-library.tools')
                  }
                />
                <Route
                  path="tool/:toolKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiLibraryTool" */ '#routes/app/projects.$projectKey.ai-library.tool.$toolKey'
                    )
                  }
                />
                <Route
                  path="datasets"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiLibraryDatasets" */ '#routes/app/projects.$projectKey.ai-library.datasets'
                    )
                  }
                />
                <Route
                  path="datasets/:datasetKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiLibraryDataset" */ '#routes/app/projects.$projectKey.ai-library.datasets.$datasetKey'
                    )
                  }
                />
                <Route
                  path="judge-templates"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiJudgeTemplates" */ '#routes/app/projects.$projectKey.ai-library.judgeTemplates'
                    )
                  }
                />
                <Route
                  path="judge-templates/:templateKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiJudgeTemplate" */ '#routes/app/projects.$projectKey.ai-library.judgeTemplates.$templateKey'
                    )
                  }
                />
                <Route
                  path="skills"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiLibrarySkills" */ '#routes/app/projects.$projectKey.ai-library.skills'
                    )
                  }
                >
                  <Route path=":skillKey" element={null} />
                </Route>
                <Route
                  path="snippets"
                  lazy={async () =>
                    import(/* webpackChunkName: "AiSnippets" */ '#routes/app/projects.$projectKey.ai-library.snippets')
                  }
                />
                <Route
                  path="snippets/new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiSnippetsNew" */ '#routes/app/projects.$projectKey.ai-library.snippets.new'
                    )
                  }
                />
                <Route
                  path="snippets/:snippetKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiSnippets" */ '#routes/app/projects.$projectKey.ai-library.snippets.$snippetKey'
                    )
                  }
                />
              </Route>
              <Route
                path="ai"
                lazy={async () => import(/* webpackChunkName: "AiLayout" */ '#routes/app/projects.$projectKey.ai_')}
              >
                <Route
                  index
                  lazy={async () =>
                    import(/* webpackChunkName: "AiLanding" */ '#routes/app/projects.$projectKey.ai.landing')
                  }
                />
                <Route
                  path="evaluation"
                  lazy={async () =>
                    import(/* webpackChunkName: "AiEvaluation" */ '#routes/app/projects.$projectKey.ai.evaluation')
                  }
                />
                <Route
                  path="playgrounds"
                  lazy={async () =>
                    import(/* webpackChunkName: "AiPlaygrounds" */ '#routes/app/projects.$projectKey.ai.playgrounds')
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiPlaygroundsIndex" */ '#routes/app/projects.$projectKey.ai.playgrounds.index'
                      )
                    }
                  />
                  <Route
                    path="active"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiPlaygroundsActive" */ '#routes/app/projects.$projectKey.ai.playgrounds.active'
                      )
                    }
                  />
                  <Route
                    path="archived"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AiPlaygroundsArchived" */ '#routes/app/projects.$projectKey.ai.playgrounds.archived'
                      )
                    }
                  />
                </Route>
                <Route
                  path="playgrounds/:evaluationId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiPlayground" */ '#routes/app/projects.$projectKey.ai.playgrounds.$evaluationId'
                    )
                  }
                />
                <Route
                  path="evaluations"
                  lazy={async () =>
                    import(/* webpackChunkName: "AiEvaluationRuns" */ '#routes/app/projects.$projectKey.ai.evaluations')
                  }
                />
                <Route
                  path="evaluations/:evaluationId/runs/:runId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AiEvaluationRun" */ '#routes/app/projects.$projectKey.ai.evaluations.$evaluationId.runs.$runId'
                    )
                  }
                />
                <Route
                  path="graphs"
                  lazy={async () =>
                    import(/* webpackChunkName: "AIAgentGraphs" */ '#routes/app/projects.$projectKey.ai.graphs')
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(/* webpackChunkName: "AIAgentGraphs" */ '#routes/app/projects.$projectKey.ai.graphs.index')
                    }
                  />
                  <Route
                    path=":graphKey"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AIAgentGraphs" */ '#routes/app/projects.$projectKey.ai.graphs.$graphKey'
                      )
                    }
                  />
                </Route>
                <Route
                  path="coding-agents"
                  lazy={async () =>
                    import(/* webpackChunkName: "AICodingAgents" */ '#routes/app/projects.$projectKey.ai.coding-agents')
                  }
                />
                <Route
                  path="optimize-agent"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent'
                    )
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.index'
                      )
                    }
                  />
                  <Route
                    path="new"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.new'
                      )
                    }
                  />
                  <Route
                    path=":optimizationId"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.$optimizationId'
                      )
                    }
                  >
                    <Route
                      index
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.$optimizationId.index'
                        )
                      }
                    />
                    <Route
                      path="runs"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.$optimizationId.runs'
                        )
                      }
                    >
                      <Route
                        index
                        lazy={async () =>
                          import(
                            /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.$optimizationId.runs.index'
                          )
                        }
                      />
                      <Route
                        path=":runId"
                        lazy={async () =>
                          import(
                            /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.$optimizationId.runs.$runId'
                          )
                        }
                      />
                    </Route>
                    <Route
                      path="configuration"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "AIOptimizeAgent" */ '#routes/app/projects.$projectKey.ai.optimize-agent.$optimizationId.configuration'
                        )
                      }
                    />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route
              path="vega"
              lazy={async () => import(/* webpackChunkName: "VegaLayout" */ '#routes/app/projects.$projectKey.vega')}
            >
              <Route
                index
                lazy={async () =>
                  import(/* webpackChunkName: "VegaLanding" */ '#routes/app/projects.$projectKey.vega.index')
                }
              />
              <Route
                path="history"
                lazy={async () =>
                  import(/* webpackChunkName: "VegaHistory" */ '#routes/app/projects.$projectKey.vega.history')
                }
              />
              <Route
                path="settings"
                lazy={async () =>
                  import(/* webpackChunkName: "VegaSettings" */ '#routes/app/projects.$projectKey.vega.settings')
                }
              />
              <Route
                path="artifacts"
                lazy={async () =>
                  import(/* webpackChunkName: "VegaArtifacts" */ '#routes/app/projects.$projectKey.vega.artifacts')
                }
              />
              <Route
                path="artifacts/:type/:externalId"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ArtifactDetail" */ '#routes/app/projects.$projectKey.vega.artifacts.$type.$externalId'
                  )
                }
              />
              <Route
                path=":conversationId"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "VegaConversation" */ '#routes/app/projects.$projectKey.vega.$conversationId'
                  )
                }
              />
            </Route>

            <Route
              path="settings"
              lazy={async () => import(/* webpackChunkName: "Settings" */ '#routes/app/projects.$projectKey.settings')}
            >
              <Route
                index
                lazy={async () =>
                  import(/* webpackChunkName: "SettingsIndex" */ '#routes/app/projects.$projectKey.settings.index')
                }
              />
              <Route
                path="context-kinds"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ContextsKindManagement" */ '#routes/app/projects.$projectKey.settings.context-kinds'
                  )
                }
              />
              {/* Compatibility redirects: AI model configs moved to AI Library */}
              <Route
                path="model-configs"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ModelConfigsRedirect" */ '#routes/app/projects.$projectKey.settings.model-configs'
                  )
                }
              />
              <Route
                path="model-configs/:configKey"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ModelConfigRedirect" */ '#routes/app/projects.$projectKey.settings.model-configs.$modelConfigKey'
                  )
                }
              />
              <Route
                path="approvals"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ApprovalSettings" */ '#routes/app/projects.$projectKey.settings.approvals'
                  )
                }
              />
              <Route
                path="views"
                lazy={async () =>
                  import(/* webpackChunkName: "ViewsSettings" */ '#routes/app/projects.$projectKey.settings.views')
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: 'ViewsOverview' */ '#routes/app/projects.$projectKey.settings.views.index'
                    )
                  }
                />
                <Route
                  path="settings"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: 'ViewsProjectSettings' */ '#routes/app/projects.$projectKey.settings.views.settings'
                    )
                  }
                />
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: 'ViewCreatorContainer' */ '#routes/app/projects.$projectKey.settings.views.new'
                    )
                  }
                />
                <Route
                  path=":viewKey/link"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: 'ViewLinkContainer' */ '#routes/app/projects.$projectKey.settings.views.$viewKey.link'
                    )
                  }
                />
                <Route
                  path=":viewKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ViewDetailContainer" */ '#routes/app/projects.$projectKey.settings.views.$viewKey'
                    )
                  }
                >
                  <Route index element={<Navigate to="linked-resources" replace />} />
                  <Route
                    path="linked-resources"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ViewLinkedResourcesContainer" */ '#routes/app/projects.$projectKey.settings.views.$viewKey.linked-resources'
                      )
                    }
                  >
                    <Route
                      index
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "ViewLinkedResourcesIndexContainer" */ '#routes/app/projects.$projectKey.settings.views.$viewKey.linked-resources.index'
                        )
                      }
                    />
                    <Route
                      path=":resource"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: 'ViewLinkedResourceContainer' */ '#routes/app/projects.$projectKey.settings.views.$viewKey.linked-resources.$resource'
                        )
                      }
                    >
                      <Route
                        path="add"
                        lazy={async () =>
                          import(
                            /* webpackChunkName: 'ViewLinkContainer' */ '#routes/app/projects.$projectKey.settings.views.$viewKey.linked-resources.$resource.add'
                          )
                        }
                      />
                    </Route>
                  </Route>

                  <Route
                    path="application"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ViewApplicationContainer" */ '#routes/app/projects.$projectKey.settings.views.$viewKey.application'
                      )
                    }
                  />

                  <Route
                    path="settings"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ViewSettingsContainer" */ '#routes/app/projects.$projectKey.settings.views.$viewKey.settings'
                      )
                    }
                  />
                </Route>
              </Route>
              <Route
                path="environments"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "EnvironmentsSettingsLayout" */ '#routes/app/projects.$projectKey.settings.environments_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "EnvironmentsSettings" */ '#routes/app/projects.$projectKey.settings.environments'
                    )
                  }
                />
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: 'EnvironmentCreatorContainer' */ '#routes/app/projects.$projectKey.settings.environments.new'
                    )
                  }
                />

                <Route
                  path=":envKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: 'EnvironmentDetails' */ '#routes/app/projects.$projectKey.settings.environments.$envKey'
                    )
                  }
                >
                  <Route index element={<Navigate to="edit" replace />} />
                  <Route
                    path="edit"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: 'EnvironmentEdit' */ '#routes/app/projects.$projectKey.settings.environments.$envKey.edit'
                      )
                    }
                  />
                  <Route
                    path="approval-settings"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: 'EnvironmentApprovalSettings' */ '#routes/app/projects.$projectKey.settings.environments.$envKey.approval-settings'
                      )
                    }
                  />
                  <Route
                    path="keys"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: 'EnvironmentKeys' */ '#routes/app/projects.$projectKey.settings.environments.$envKey.keys'
                      )
                    }
                  />
                </Route>
              </Route>
              <Route
                path="payload-filters"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "PayloadFiltersSettings_" */ '#routes/app/projects.$projectKey.settings.payload-filters_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "PayloadFiltersSettings" */ '#routes/app/projects.$projectKey.settings.payload-filters'
                    )
                  }
                />
                <Route
                  path=":filterKey/edit"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "PayloadFiltersEditSettings" */ '#routes/app/projects.$projectKey.settings.payload-filters.$filterKey.edit'
                    )
                  }
                />
              </Route>

              <Route
                path="flags"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ProjectFlagSettings" */ '#routes/app/projects.$projectKey.settings.flags'
                  )
                }
              />

              <Route
                path="release-pipelines"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationReleasePipelinesIndex" */ '#routes/app/projects.$projectKey.settings.release-pipelines'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "OrganizationReleasePipelinesIndex" */ '#routes/app/projects.$projectKey.settings.release-pipelines.index'
                    )
                  }
                />
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "OrganizationReleasePipelineNew" */ '#routes/app/projects.$projectKey.settings.release-pipelines.new'
                    )
                  }
                />
                <Route
                  path=":releasePipelineKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "OrganizationReleasePipelineDetails" */ '#routes/app/projects.$projectKey.settings.release-pipelines.$releasePipelineKey'
                    )
                  }
                />
              </Route>

              <Route
                path="releases"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationReleases" */ '#routes/app/projects.$projectKey.settings.releases'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "OrganizationReleasesIndex" */ '#routes/app/projects.$projectKey.settings.releases.index'
                    )
                  }
                />

                <Route
                  path="release-pipelines"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "OrganizationReleasePipelinesIndex" */ '#routes/app/projects.$projectKey.settings.releases.release-pipelines'
                    )
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "OrganizationReleasePipelineNew" */ '#routes/app/projects.$projectKey.settings.releases.release-pipelines.new'
                      )
                    }
                  />
                  <Route
                    path=":releasePipelineKey"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "OrganizationReleasePipelineDetails" */ '#routes/app/projects.$projectKey.settings.releases.release-pipelines.$releasePipelineKey'
                      )
                    }
                  />
                </Route>

                <Route
                  path="release-policies"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "OrganizationReleasePolicies" */ '#routes/app/projects.$projectKey.settings.releases.release-policies'
                    )
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "OrganizationReleasePolicyNew" */ '#routes/app/projects.$projectKey.settings.releases.release-policies.new'
                      )
                    }
                  />

                  <Route
                    path=":releasePolicyKey"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "OrganizationReleasePolicyDetails" */ '#routes/app/projects.$projectKey.settings.releases.release-policies.$releasePolicyKey'
                      )
                    }
                  />
                </Route>
              </Route>

              <Route
                path="observability"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ProjectObservabilitySettings_" */ '#routes/app/projects.$projectKey.settings.observability_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ProjectObservabilitySettings" */ '#routes/app/projects.$projectKey.settings.observability'
                    )
                  }
                />
                <Route
                  path="filter-rules/:product"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ProjectObservabilityFilterRulesSettings" */ '#routes/app/projects.$projectKey.settings.observability.filter-rules.$product'
                    )
                  }
                />
              </Route>
              <Route
                path="lifecycle"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "LifecycleSettings" */ '#routes/app/projects.$projectKey.settings.lifecycle'
                  )
                }
              />
              <Route
                path="vega"
                lazy={async () =>
                  import(/* webpackChunkName: "ProjectVegaSettings" */ '#routes/app/projects.$projectKey.settings.vega')
                }
              />
            </Route>

            <Route handle={{ navMode: 'code' }}>
              <Route
                path="contexts"
                lazy={async () =>
                  import(/* webpackChunkName: "Contexts" */ '#routes/app/projects.$projectKey.contexts')
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(/* webpackChunkName: "ContextsIndex" */ '#routes/app/projects.$projectKey.contexts.index')
                  }
                />
                <Route path="kinds">
                  <Route
                    index
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ContextsKindManagement" */ '#routes/app/projects.$projectKey.contexts.kinds.index'
                      )
                    }
                  />
                </Route>
                <Route
                  path="kinds/:kind/instances/:key"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ContextsDetails" */ '#routes/app/projects.$projectKey.contexts.$kind-$key'
                    )
                  }
                />
                <Route
                  path="instances/:instanceId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ContextInstanceDetails" */ '#routes/app/projects.$projectKey.contexts.$instanceId'
                    )
                  }
                />
              </Route>
              <Route
                path="segments"
                lazy={async () =>
                  import(/* webpackChunkName: "Segments" */ '#routes/app/projects.$projectKey.segments')
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(/* webpackChunkName: "SegmentsIndex" */ '#routes/app/projects.$projectKey.segments.index')
                  }
                />
                <Route
                  lazy={async () =>
                    import(/* webpackChunkName: "SegmentsIndex" */ '#routes/app/projects.$projectKey.segments.index')
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(/* webpackChunkName: "SegmentsNew" */ '#routes/app/projects.$projectKey.segments.new')
                    }
                  />
                </Route>
                <Route
                  path=":segmentKey"
                  lazy={async () =>
                    import(/* webpackChunkName: "Segment" */ '#routes/app/projects.$projectKey.segments.$segmentKey')
                  }
                >
                  <Route index element={<Navigate to="targeting" replace />} />
                  <Route
                    path="targeting"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "SegmentTargeting" */ '#routes/app/projects.$projectKey.segments.$segmentKey.targeting'
                      )
                    }
                  >
                    <Route
                      path="exports/:exportKey"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "SegmentDownload" */ '#routes/app/projects.$projectKey.segments.$segmentKey.targeting.exports.$exportKey'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="flags"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "SegmentFlags" */ '#routes/app/projects.$projectKey.segments.$segmentKey.flags'
                      )
                    }
                  />
                  <Route
                    path="overview"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "SegmentOverview" */ '#routes/app/projects.$projectKey.segments.$segmentKey.overview'
                      )
                    }
                  />
                  <Route
                    path="history/*"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "SegmentHistory" */ '#routes/app/projects.$projectKey.segments.$segmentKey.history'
                      )
                    }
                  />
                  <Route
                    path="history/:auditId/*"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "SegmentHistoryAuditEntryHome" */ '#routes/app/projects.$projectKey.segments.$segmentKey.history.$auditId'
                      )
                    }
                  />
                  <Route
                    path="settings"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "SegmentSettings" */ '#routes/app/projects.$projectKey.segments.$segmentKey.settings'
                      )
                    }
                  />
                </Route>
              </Route>
              <Route
                path="live"
                lazy={async () => import(/* webpackChunkName: "LiveEvents" */ '#routes/app/projects.$projectKey.live')}
              >
                <Route
                  path="flags/:flagKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "LiveEventsFlags" */ '#routes/app/projects.$projectKey.live.flags.$flagKey'
                    )
                  }
                />
              </Route>
            </Route>
            <Route
              path="holdouts"
              lazy={async () => import(/* webpackChunkName: "Holdouts" */ '#routes/app/projects.$projectKey.holdouts')}
            >
              <Route
                lazy={async () =>
                  import(
                    /* webpackChunkName: "HoldoutsDashboardLayout" */ '#routes/app/projects.$projectKey.holdouts.dashboard'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "HoldoutDashboardIndex" */ '#routes/app/projects.$projectKey.holdouts.dashboard.index'
                    )
                  }
                />
                <Route
                  path="new"
                  lazy={async () =>
                    import(/* webpackChunkName: "HoldoutNew" */ '#routes/app/projects.$projectKey.holdouts.new')
                  }
                />
              </Route>
              <Route
                path=":holdoutKey"
                lazy={async () =>
                  import(/* webpackChunkName: "Holdout" */ '#routes/app/projects.$projectKey.holdouts.$holdoutKey')
                }
              >
                <Route
                  path="design"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "HoldoutDesign" */ '#routes/app/projects.$projectKey.holdouts.$holdoutKey.design'
                    )
                  }
                />
                <Route
                  path="details"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "HoldoutDetails" */ '#routes/app/projects.$projectKey.holdouts.$holdoutKey.details'
                    )
                  }
                />
              </Route>
            </Route>
            <Route
              path="experiments"
              lazy={async () =>
                import(/* webpackChunkName: "Experiments" */ '#routes/app/projects.$projectKey.experiments')
              }
            >
              {/* Experiment dashboard */}
              <Route
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ExperimentsDashboardLayout" */ '#routes/app/projects.$projectKey.experiments.dashboard'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentDashboardIndex" */ '#routes/app/projects.$projectKey.experiments.dashboard.index'
                    )
                  }
                />

                <Route
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentDashboardIndex" */ '#routes/app/projects.$projectKey.experiments.dashboard.index'
                    )
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(/* webpackChunkName: "ExperimentNew" */ '#routes/app/projects.$projectKey.experiments.new')
                    }
                  />
                </Route>
                <Route
                  path=":experimentId/details"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentDetails" */ '#routes/app/projects.$projectKey.experiments.$experimentId.details'
                    )
                  }
                />
              </Route>

              {/* Experiment details */}
              <Route
                path=":experimentKey"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "Experiment" */ '#routes/app/projects.$projectKey.experiments.$experimentKey'
                  )
                }
              >
                <Route
                  path="design"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentDesign" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.design'
                    )
                  }
                >
                  <Route
                    path="pending-changes"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ExperimentPendingChanges" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.design.pending-changes'
                      )
                    }
                  />
                </Route>
                <Route
                  path="results"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentResults" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.results'
                    )
                  }
                >
                  <Route
                    path="pending-changes"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "ExperimentResultsPendingChanges" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.results.pending-changes'
                      )
                    }
                  />
                </Route>
                <Route
                  path="iterations"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentIterations" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.iterations'
                    )
                  }
                />
                <Route
                  path="iterations/:iterationId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentIteration" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.iterations.$iterationId'
                    )
                  }
                />
                <Route
                  path="traffic"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentTraffic" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.traffic'
                    )
                  }
                />
                <Route
                  path="analytics"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentAnalytics" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.analytics'
                    )
                  }
                />
                <Route
                  path="exposures"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ExperimentExposures" */ '#routes/app/projects.$projectKey.experiments.$experimentKey.exposures'
                    )
                  }
                />
              </Route>
              {/* This route has a different layout so it stays outside of the other nested routes */}
            </Route>
            {/* End of experimentation routes */}

            {/* Multi-armed bandits routes */}
            <Route
              path="multi-armed-bandits"
              lazy={async () =>
                import(
                  /* webpackChunkName: "MultiArmedBandits" */ '#routes/app/projects.$projectKey.multi-armed-bandits'
                )
              }
            >
              {/* Multi-armed bandits dashboard */}
              <Route
                lazy={async () =>
                  import(
                    /* webpackChunkName: "MultiArmedBanditDashboardLayout" */ '#routes/app/projects.$projectKey.multi-armed-bandits.dashboard'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MultiArmedBanditDashboardIndex" */ '#routes/app/projects.$projectKey.multi-armed-bandits.dashboard.index'
                    )
                  }
                />

                <Route
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MultiArmedBanditsDashboardIndex" */ '#routes/app/projects.$projectKey.multi-armed-bandits.dashboard.index'
                    )
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "MultiArmedBanditsNew" */ '#routes/app/projects.$projectKey.multi-armed-bandits.new'
                      )
                    }
                  />
                </Route>
              </Route>
              {/* Multi-armed bandit details */}
              <Route
                path=":multiArmedBanditKey"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "MultiArmedBandit" */ '#routes/app/projects.$projectKey.multi-armed-bandits.$multiArmedBanditKey'
                  )
                }
              >
                <Route
                  path="design"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MultiArmedBanditDesign" */ '#routes/app/projects.$projectKey.multi-armed-bandits.$multiArmedBanditKey.design'
                    )
                  }
                />
                <Route
                  path="results"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MultiArmedBanditResults" */ '#routes/app/projects.$projectKey.multi-armed-bandits.$multiArmedBanditKey.results'
                    )
                  }
                />
                <Route
                  path="iterations"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MultiArmedBanditIterations" */ '#routes/app/projects.$projectKey.multi-armed-bandits.$multiArmedBanditKey.iterations'
                    )
                  }
                />
                <Route
                  path="iterations/:iterationId"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MultiArmedBanditIteration" */ '#routes/app/projects.$projectKey.multi-armed-bandits.$multiArmedBanditKey.iterations.$iterationId'
                    )
                  }
                />
                <Route
                  path="exposures"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MultiArmedBanditExposures" */ '#routes/app/projects.$projectKey.multi-armed-bandits.$multiArmedBanditKey.exposures'
                    )
                  }
                />
              </Route>
              {/* This route has a different layout so it stays outside of the other nested routes */}
            </Route>
            {/* End of multi-armed-bandits routes */}

            <Route
              path="metrics"
              lazy={async () => import(/* webpackChunkName: "Metrics_" */ '#routes/app/projects.$projectKey.metrics_')}
            >
              <Route
                lazy={async () => import(/* webpackChunkName: "Metrics" */ '#routes/app/projects.$projectKey.metrics')}
              >
                <Route
                  index
                  lazy={async () =>
                    import(/* webpackChunkName: "MetricsIndex" */ '#routes/app/projects.$projectKey.metrics.index')
                  }
                />
                <Route
                  lazy={async () =>
                    import(/* webpackChunkName: "MetricsIndex" */ '#routes/app/projects.$projectKey.metrics.index')
                  }
                >
                  <Route
                    path="new"
                    lazy={async () =>
                      import(/* webpackChunkName: "MetricsNew" */ '#routes/app/projects.$projectKey.metrics.new')
                    }
                  />
                </Route>
              </Route>

              <Route
                path=":metricKey/details"
                lazy={async () =>
                  import(/* webpackChunkName: "Metric" */ '#routes/app/projects.$projectKey.metrics.$metricKey.details')
                }
              />
              <Route
                path="groups"
                lazy={async () =>
                  import(/* webpackChunkName: "MetricsGroups_" */ '#routes/app/projects.$projectKey.metrics.groups_')
                }
              >
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MetricGroupsNew" */ '#routes/app/projects.$projectKey.metrics.groups.new'
                    )
                  }
                />
                <Route
                  path=":metricGroupKey/details"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "MetricGroupsDetails" */ '#routes/app/projects.$projectKey.metrics.groups.$metricGroupKey.details'
                    )
                  }
                />
              </Route>
            </Route>
            <Route
              path="observe"
              lazy={async () => import(/* webpackChunkName: "Observe" */ '#routes/app/projects.$projectKey.observe')}
            >
              <Route
                path="new-metric"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ObserveNewMetric" */ '#routes/app/projects.$projectKey.observe.new-metric'
                  )
                }
              />
              <Route path="expand/:dataSourceKey/:eventKind/:eventKey" element={null} />
            </Route>
            <Route
              path="approvals"
              lazy={async () =>
                import(/* webpackChunkName: "ApprovalDashboard_" */ '#routes/app/projects.$projectKey.approvals_')
              }
            >
              <Route
                index
                lazy={async () =>
                  import(/* webpackChunkName: "ApprovalDashboard" */ '#routes/app/projects.$projectKey.approvals')
                }
              />
              <Route
                path=":approvalRequestId"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "Approval" */ '#routes/app/projects.$projectKey.approvals.$approvalRequestId'
                  )
                }
              />
            </Route>
            <Route
              path="metric-data-sources"
              lazy={async () =>
                import(
                  /* webpackChunkName: "MetricsDataSources_" */ '#routes/app/projects.$projectKey.metric-data-sources_'
                )
              }
            >
              <Route
                index
                lazy={async () =>
                  import(
                    /* webpackChunkName: "MetricDataSourcesList" */ '#routes/app/projects.$projectKey.metric-data-sources.index'
                  )
                }
              />
              <Route
                path="new"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "MetricDataSourcesCreate" */ '#routes/app/projects.$projectKey.metric-data-sources.new'
                  )
                }
              />
              <Route
                path=":dataSourceKey/details"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "MetricDataSourcesDetails" */ '#routes/app/projects.$projectKey.metric-data-sources.$dataSourceKey.details'
                  )
                }
              />
            </Route>
            <Route
              path="data-sources"
              lazy={async () =>
                import(/* webpackChunkName: "DataSources" */ '#routes/app/projects.$projectKey.data-sources')
              }
            >
              <Route
                index
                lazy={async () =>
                  import(
                    /* webpackChunkName: "DataSourcesIndex" */ '#routes/app/projects.$projectKey.data-sources.index'
                  )
                }
              />
              <Route
                path="metrics"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "DataSourcesMetrics_" */ '#routes/app/projects.$projectKey.data-sources.metrics_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "DataSourcesMetrics" */ '#routes/app/projects.$projectKey.data-sources.metrics.index'
                    )
                  }
                />
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "DataSourcesMetricsCreate" */ '#routes/app/projects.$projectKey.data-sources.metrics.new'
                    )
                  }
                />
                <Route
                  path=":dataSourceKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "DataSourcesMetricsDetails" */ '#routes/app/projects.$projectKey.data-sources.metrics.$dataSourceKey'
                    )
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "DataSourcesMetricsDetailsIndex" */ '#routes/app/projects.$projectKey.data-sources.metrics.$dataSourceKey.index'
                      )
                    }
                  />
                  <Route
                    path="connections"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "DataSourcesMetricsDetailsConnections" */ '#routes/app/projects.$projectKey.data-sources.metrics.$dataSourceKey.connections'
                      )
                    }
                  >
                    <Route
                      path="new-metric"
                      lazy={async () =>
                        import(
                          /* webpackChunkName: "DataSourcesMetricsDetailsConnectionsCreateMetric" */ '#routes/app/projects.$projectKey.data-sources.metrics.$dataSourceKey.connections.new-metric'
                        )
                      }
                    />
                  </Route>
                  <Route
                    path="configuration"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "DataSourcesMetricsDetailsConfiguration" */ '#routes/app/projects.$projectKey.data-sources.metrics.$dataSourceKey.configuration'
                      )
                    }
                  />
                </Route>
              </Route>
              <Route
                path="assignments"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "DataSourcesAssignments_" */ '#routes/app/projects.$projectKey.data-sources.assignments_'
                  )
                }
              >
                <Route
                  index
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "DataSourcesAssignments" */ '#routes/app/projects.$projectKey.data-sources.assignments.index'
                    )
                  }
                />
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "DataSourcesAssignmentsCreate" */ '#routes/app/projects.$projectKey.data-sources.assignments.new'
                    )
                  }
                />
                <Route
                  path=":dataSourceKey"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "DataSourcesAssignmentsDetails" */ '#routes/app/projects.$projectKey.data-sources.assignments.$dataSourceKey'
                    )
                  }
                >
                  <Route
                    index
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "DataSourcesAssignmentsDetailsIndex" */ '#routes/app/projects.$projectKey.data-sources.assignments.$dataSourceKey.index'
                      )
                    }
                  />
                  <Route
                    path="connections"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "DataSourcesAssignmentsDetailsConnections" */ '#routes/app/projects.$projectKey.data-sources.assignments.$dataSourceKey.connections'
                      )
                    }
                  />
                  <Route
                    path="configuration"
                    lazy={async () =>
                      import(
                        /* webpackChunkName: "DataSourcesAssignmentsDetailsConfiguration" */ '#routes/app/projects.$projectKey.data-sources.assignments.$dataSourceKey.configuration'
                      )
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route
            path="sdk-setup"
            lazy={async () => import(/* webpackChunkName: "SdkSetup" */ '#routes/app/projects.$projectKey.sdk-setup')}
          />
        </Route>

        <Route path="/profile" lazy={async () => import(/* webpackChunkName: "Profile" */ '#routes/app/profile')}>
          <Route index lazy={async () => import(/* webpackChunkName: "ProfileIndex" */ '#routes/app/profile.index')} />
        </Route>

        <Route
          path="/github-connect"
          lazy={async () => import(/* webpackChunkName: "GithubConnect" */ '#routes/app/github-connect')}
        />

        <Route
          path="/settings"
          lazy={async () => import(/* webpackChunkName: "OrganizationSettings" */ '#routes/app/settings')}
        >
          <Route index element={<Navigate to="general" replace />} />
          <Route
            path="general"
            lazy={async () =>
              import(/* webpackChunkName: "OrganizationGeneralSettings" */ '#routes/app/settings.general')
            }
          />
          <Route
            path="members"
            lazy={async () => import(/* webpackChunkName: "OrganizationMembers_" */ '#routes/app/settings.members_')}
          >
            <Route
              index
              lazy={async () => import(/* webpackChunkName: "OrganizationMembers" */ '#routes/app/settings.members')}
            />
            <Route
              lazy={async () => import(/* webpackChunkName: "OrganizationMembers" */ '#routes/app/settings.members')}
            >
              <Route
                path="invite/:inviteEmail?"
                lazy={async () =>
                  import(/* webpackChunkName: "OrganizationMembersInvite" */ '#routes/app/settings.members.invite')
                }
              />
            </Route>
            <Route
              path=":memberId"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationMember" */ '#routes/app/settings.members.$memberId')
              }
            >
              <Route
                index
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationMemberIndex" */ '#routes/app/settings.members.$memberId.index'
                  )
                }
              />
              <Route
                path="access-summary"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationMemberAccessSummary" */ '#routes/app/settings.members.$memberId.access-summary'
                  )
                }
              />
              <Route
                path="permissions"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationMemberPermissions" */ '#routes/app/settings.members.$memberId.permissions'
                  )
                }
              />
              <Route
                path="teams"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationMemberTeams" */ '#routes/app/settings.members.$memberId.teams'
                  )
                }
              />
              <Route
                path="history/*"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationMemberHistory" */ '#routes/app/settings.members.$memberId.history'
                  )
                }
              />
              <Route
                path="settings"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationMemberSettings" */ '#routes/app/settings.members.$memberId.settings'
                  )
                }
              />
            </Route>
          </Route>
          <Route
            path="teams"
            lazy={async () => import(/* webpackChunkName: "OrganizationTeams_" */ '#routes/app/settings.teams_')}
          >
            <Route
              index
              lazy={async () => import(/* webpackChunkName: "OrganizationTeams" */ '#routes/app/settings.teams')}
            />
            <Route
              path=":teamKey"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationTeam" */ '#routes/app/settings.teams.$teamKey')
              }
            >
              <Route
                index
                lazy={async () =>
                  import(/* webpackChunkName: "OrganizationTeamIndex" */ '#routes/app/settings.teams.$teamKey.index')
                }
              />
              <Route
                path="access-summary"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationTeamAccessSummary" */ '#routes/app/settings.teams.$teamKey.access-summary'
                  )
                }
              />
              <Route
                path="members"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationTeamMembers" */ '#routes/app/settings.teams.$teamKey.members'
                  )
                }
              />
              <Route
                path="permissions"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationTeamPermissions" */ '#routes/app/settings.teams.$teamKey.permissions'
                  )
                }
              />
              <Route
                path="history/*"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationTeamHistory" */ '#routes/app/settings.teams.$teamKey.history'
                  )
                }
              />
              <Route
                path="settings"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationTeamSettings" */ '#routes/app/settings.teams.$teamKey.settings'
                  )
                }
              />
            </Route>
          </Route>
          <Route path="projects">
            <Route
              index
              lazy={async () => import(/* webpackChunkName: "OrganizationProjects" */ '#routes/app/settings.projects')}
            />
          </Route>
          <Route
            path="applications"
            lazy={async () => import(/* webpackChunkName: "Applications" */ '#routes/app/settings.applications')}
          >
            <Route index lazy={ApplicationsListContainer} />
            <Route lazy={ApplicationsListContainer}>
              <Route
                path="new"
                lazy={async () =>
                  import(/* webpackChunkName: "ApplicationsNew" */ '#routes/app/settings.applications.new')
                }
              />
              <Route
                path=":applicationKey/edit"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ApplicationEdit" */ '#routes/app/settings.applications.$applicationKey.edit'
                  )
                }
              />
            </Route>
            <Route
              path=":applicationKey"
              lazy={async () =>
                import(/* webpackChunkName: "ApplicationLayout" */ '#routes/app/settings.applications.$applicationKey')
              }
            >
              <Route index element={<Navigate to="versions" replace />} />
              <Route
                path="versions"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ApplicationVersions" */ '#routes/app/settings.applications.$applicationKey.versions'
                  )
                }
              >
                <Route
                  path="new"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ApplicationVersionsNew" */ '#routes/app/settings.applications.$applicationKey.versions.new'
                    )
                  }
                />
                <Route
                  path=":versionKey/support-status"
                  lazy={async () =>
                    import(
                      /* webpackChunkName: "ApplicationVersionSupportStatus" */ '#routes/app/settings.applications.$applicationKey.versions.support-status'
                    )
                  }
                />
              </Route>

              <Route
                path="history/*"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "ApplicationHistory" */ '#routes/app/settings.applications.$applicationKey.history'
                  )
                }
              />
            </Route>
          </Route>
          <Route path="roles" lazy={async () => import(/* webpackChunkName: "Roles" */ '#routes/app/settings.roles_')}>
            <Route
              index
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationRolesIndex" */ '#routes/app/settings.roles.index')
              }
            />

            {/* UPDATED ROLES */}
            <Route lazy={async () => import(/* webpackChunkName: "OrganizationRoles" */ '#routes/app/settings.roles')}>
              <Route
                path="project"
                lazy={async () => import(/* webpackChunkName: "ProjectRoles"*/ '#routes/app/settings.roles.project')}
              >
                <Route
                  path=":key/view"
                  lazy={async () =>
                    import(/* webpackChunkName: "OrganizationRolesView" */ '#routes/app/settings.roles.$key.view')
                  }
                />
              </Route>
              <Route
                path="organization"
                lazy={async () => import(/* webpackChunkName: "OrgRoles"*/ '#routes/app/settings.roles.organization')}
              >
                <Route
                  path=":key/view"
                  lazy={async () =>
                    import(/* webpackChunkName: "OrganizationRolesView" */ '#routes/app/settings.roles.$key.view')
                  }
                />
              </Route>
            </Route>

            <Route
              path="new"
              lazy={async () => import(/* webpackChunkName: "OrganizationRolesNew" */ '#routes/app/settings.roles.new')}
            />
            <Route
              path=":key/edit"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationRolesEdit" */ '#routes/app/settings.roles.$key.edit')
              }
            />
            <Route
              path=":key/view"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationRolesView" */ '#routes/app/settings.roles.$key.view')
              }
            />
          </Route>

          <Route
            path="relay"
            lazy={async () => import(/* webpackChunkName: "OrganizationRelayProxies" */ '#routes/app/settings.relay')}
          >
            <Route
              path="new"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationRelayProxiesNew" */ '#routes/app/settings.relay.new')
              }
            />
            <Route
              path=":id/edit"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationRelayProxiesEdit" */ '#routes/app/settings.relay.$id.edit')
              }
            />
          </Route>
          <Route
            path="security"
            lazy={async () => import(/* webpackChunkName: "OrganizationSecurity" */ '#routes/app/settings.security')}
          >
            <Route
              path="saml/config"
              lazy={async () =>
                import(/* webpackChunkName: 'OrganizationSAMLConfig' */ '#routes/app/settings.security.saml.config')
              }
            />
          </Route>
          <Route
            path="authorization"
            lazy={async () =>
              import(/* webpackChunkName: "OrganizationAuthorization" */ '#routes/app/settings.authorization')
            }
          >
            {/*<Route index element={<Navigate to="tokens" replace />} />*/}
            <Route
              path="roles/:key"
              lazy={async () =>
                import(
                  /* webpackChunkName: "OrganizationAuthorizationViewRole" */ '#routes/app/settings.authorization.roles.$key'
                )
              }
            />
            <Route
              path="tokens"
              lazy={async () =>
                import(
                  /* webpackChunkName: "OrganizationAuthorizationTokens" */ '#routes/app/settings.authorization.tokens'
                )
              }
            >
              <Route
                index
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationAuthorizationTokensIndex" */ '#routes/app/settings.authorization.tokens.index'
                  )
                }
              />
              <Route
                path="new"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationAuthorizationTokenNew" */ '#routes/app/settings.authorization.tokens.new'
                  )
                }
              />
              <Route
                path=":tokenId/clone"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationAuthorizationTokenClone" */ '#routes/app/settings.authorization.tokens.$tokenId.clone'
                  )
                }
              />
              <Route
                path=":tokenId/edit"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationAuthorizationTokenEdit" */ '#routes/app/settings.authorization.tokens.$tokenId.edit'
                  )
                }
              />
              <Route
                path=":tokenId/reset"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationAuthorizationTokenReset" */ '#routes/app/settings.authorization.tokens.$tokenId.reset'
                  )
                }
              />
            </Route>
            <Route
              path="oauth"
              lazy={async () =>
                import(
                  /* webpackChunkName: "OrganizationAuthorizationOAuth" */ '#routes/app/settings.authorization.oauth'
                )
              }
            >
              <Route
                index
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationAuthorizationOAuthIndex" */ '#routes/app/settings.authorization.oauth.index'
                  )
                }
              />
              <Route
                path=":appId/review"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationAuthorizationOAuthReview" */ '#routes/app/settings.authorization.oauth.$appId.review'
                  )
                }
              />
            </Route>
          </Route>
          <Route
            path="sdk-keys"
            lazy={async () => import(/* webpackChunkName: "OrganizationSdkKeys" */ '#routes/app/settings.sdk-keys')}
          />
          <Route path="tokens/*" element={<Navigate to="../authorization" replace />} />
          <Route
            path="billing"
            lazy={async () => import(/* webpackChunkName: "OrganizationBilling" */ '#routes/app/settings.billing')}
          >
            <Route
              index
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationBillingIndex" */ '#routes/app/settings.billing.index')
              }
            />
            <Route
              path="plans"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationBillingPlans" */ '#routes/app/settings.billing.plans')
              }
            >
              <Route
                index
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationBillingPlansIndex" */ '#routes/app/settings.billing.plans.index'
                  )
                }
              />
              <Route
                path="customize"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationBillingPlanCustomize" */ '#routes/app/settings.billing.plans.customize'
                  )
                }
              />
              <Route
                path="review"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationBillingPlanReview" */ '#routes/app/settings.billing.plans.review'
                  )
                }
              />
              <Route
                path="confirmation"
                lazy={async () =>
                  import(
                    /* webpackChunkName: "OrganizationBillingPlanConfirmation" */ '#routes/app/settings.billing.plans.confirmation'
                  )
                }
              />
            </Route>
          </Route>
          <Route
            path="plan-usage"
            lazy={async () => import(/* webpackChunkName: "OrganizationPlanUsage" */ '#routes/app/settings.plan-usage')}
          >
            <Route index element={<Navigate to="details/overview" replace />} />
            <Route
              path="details/overview"
              lazy={async () =>
                import(
                  /* webpackChunkName: "OrganizationPlanUsageOverview" */ '#routes/app/settings.plan-usage.details.overview'
                )
              }
            />
            <Route
              path="details/:metric"
              lazy={async () =>
                import(
                  /* webpackChunkName: "OrganizationPlanUsageDetail" */ '#routes/app/settings.plan-usage.details.$metric'
                )
              }
            />
          </Route>
          <Route
            path="diagnostic-usage"
            lazy={async () =>
              import(/* webpackChunkName: "OrganizationDiagnosticUsage" */ '#routes/app/settings.diagnostic-usage')
            }
          >
            <Route index element={<Navigate to="details/overview" replace />} />
            <Route
              path="details/overview"
              lazy={async () =>
                import(
                  /* webpackChunkName: "OrganizationDiagnosticUsageOverview" */ '#routes/app/settings.diagnostic-usage.details.overview'
                )
              }
            />
            <Route
              path="details/:metric"
              lazy={async () =>
                import(
                  /* webpackChunkName: "OrganizationDiagnosticUsageDetail" */ '#routes/app/settings.diagnostic-usage.details.$metric'
                )
              }
            />
          </Route>
          <Route
            path="sdks"
            lazy={async () => import(/* webpackChunkName: "OrganizationSDKs" */ '#routes/app/settings.sdks')}
          >
            <Route
              path=":applicationId"
              lazy={async () =>
                import(/* webpackChunkName: "OrganizationSDKDetail" */ '#routes/app/settings.sdks.$applicationId')
              }
            />
          </Route>
          <Route
            path="token-limits"
            lazy={async () =>
              import(/* webpackChunkName: "OrganizationTokenLimits" */ '#routes/app/settings.token-limits')
            }
          />
          <Route
            path="factory"
            lazy={async () => import(/* webpackChunkName: "OrganizationFactory" */ '#routes/app/settings.factory')}
          />
          {/* Deprecated: code-automation redirects to factory */}
          <Route path="code-automation" element={<Navigate to="../factory" replace />} />
          {/* Deprecated: usage routes redirect to plan-usage */}
          <Route path="usage" element={<Navigate to="../plan-usage" replace />} />
          <Route path="usage/details/*" element={<Navigate to="../plan-usage" replace />} />
          <Route
            path="integrations"
            lazy={async () => import(/* webpackChunkName: "Integrations" */ '#routes/app/settings.integrations')}
          >
            <Route
              path="destinations/new"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsDestinationsNew" */ '#routes/app/settings.integrations.destinations.new'
                )
              }
            />
            <Route
              path="destinations/:destinationId/edit"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsDestinationsNew" */ '#routes/app/settings.integrations.destinations.edit'
                )
              }
            />
            <Route
              path="webhooks/new"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsWebhookNew" */ '#routes/app/settings.integrations.webhooks.new'
                )
              }
            />
            <Route
              path="webhooks/:webhookId/edit"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsWebhookEdit" */ '#routes/app/settings.integrations.webhooks.edit'
                )
              }
            />
            <Route
              path="slack/new"
              lazy={async () =>
                import(/* webpackChunkName: "IntegrationsSlackNew" */ '#routes/app/settings.integrations.slack.new')
              }
            />
            <Route
              path="slack/:slackHookId/edit"
              lazy={async () =>
                import(/* webpackChunkName: "IntegrationsSlackEdit" */ '#routes/app/settings.integrations.slack.edit')
              }
            />
            <Route
              path="coderefs/:id/edit"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsRepositoryEdit" */ '#routes/app/settings.integrations.repositories.edit'
                )
              }
            />
            <Route
              path=":kind/new"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsGoaltenderSubscriptionNew" */ '#routes/app/settings.integrations.goaltender.subscriptions.new'
                )
              }
            />
            <Route
              path=":kind/:subscriptionId/errors/view"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsGoaltenderSubscriptionErrors" */ '#routes/app/settings.integrations.goaltender.subscriptions.errors'
                )
              }
            />
            <Route
              path=":kind/:subscriptionId/edit"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsGoaltenderSubscriptionEdit" */ '#routes/app/settings.integrations.goaltender.subscriptions.edit'
                )
              }
            />
            <Route
              path="sentry/show"
              lazy={async () =>
                import(/* webpackChunkName: "IntegrationsSentry" */ '#routes/app/settings.integrations.sentry')
              }
            />
            <Route
              path="github-app/show"
              lazy={async () =>
                import(/* webpackChunkName: "IntegrationsGithubApp" */ '#routes/app/settings.integrations.github-app')
              }
            />
            <Route
              path=":kind/show"
              lazy={async () =>
                import(
                  /* webpackChunkName: "IntegrationsGoaltenderLearnMore" */ '#routes/app/settings.integrations.goaltender.learn-more'
                )
              }
            />
          </Route>
          <Route
            path="history/*"
            lazy={async () => import(/* webpackChunkName: "OrganizationHistory" */ '#routes/app/settings.history')}
          />
          <Route path="profile" element={<Navigate to="/profile" replace />} />
        </Route>
      </Route>
      <Route
        path="/internal/new-hires"
        lazy={async () => import(/* webpackChunkName: "newHires.entry" */ '#components/NewHires/NewHiresContainer')}
      />
    </Route>
    <Route
      path="/verify-email"
      lazy={async () => import(/* webpackChunkName: "EmailVerification" */ '#routes/app/verify-email')}
    />
    <Route
      path="/welcome-survey"
      lazy={async () => import(/* webpackChunkName: "WelcomeSurvey" */ '#routes/app/welcome-survey')}
    />
    {/* special unauthenticated route for privilege escalation (sometimes called "beast mode") */}
    <Route
      path="/settings/escalate"
      lazy={async () => import(/* webpackChunkName: "EscalatePrivileges" */ '#components/EscalatePrivilegesContainer')}
    />
    <Route
      path="/confirm-your-email"
      lazy={async () => import(/* webpackChunkName: "ConfirmEmailOwnership" */ '#routes/app/confirm-your-email')}
    />
    <Route path="*" lazy={async () => import(/* webpackChunkName: "404NotFound" */ '#routes/app/404')} />
  </Route>
);

function OwnershipWatcher() {
  const routes = useMemo(() => appRoutes(), []);
  const matchingPathPattern = useMatchingAppPathPattern(routes);

  useEffect(() => {
    if (matchingPathPattern) {
      const owner = appRouteOwnership[matchingPathPattern];
      setRouteContext(matchingPathPattern, owner);
    }
  }, [matchingPathPattern]);

  return <Outlet />;
}
